import { useCallback, useEffect, useRef, useState } from 'react';
import { App } from 'antd';

import { api, readTotalCount } from '../api/client';
import { API_CARD_DEFS_URL } from '../utils';

/**
 * The cards LIST — what the cards page renders. Distinct from the app-wide
 * card-definitions cache (CardDefinitionsContext), which stays unfiltered
 * because every picker, title, and vocabulary reads from it: this hook asks
 * the same endpoint for the caller's readable cards narrowed by a KQL
 * `query` (`?q=`, cards subject — docs/plans/list-queries.md) and annotated
 * with `include=usage`, so each card carries
 * `usage: { childCount, collections[], ownerCollection }` for the tile pills.
 *
 * The response is a bare array plus an `X-Total-Count` header (the count
 * falls back to the array's length). A generation guard drops responses from
 * before the latest query change. The only 400 a filtered list gets is a
 * malformed `q`; with `onQueryError` given, that message goes to the caller
 * (the page drops the query and toasts, as the items page does) instead of
 * the generic failure toast.
 */
export default function useCardList({ query, onQueryError } = {}) {
	const { message } = App.useApp();
	const [cards, setCards] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const generationRef = useRef(0);

	const fetchList = useCallback((generation) => {
		const params = { include: 'usage' };
		// Blank is simply no filter; the server ANDs q with the caller's scope.
		if (query) params.q = query;

		return api.get(API_CARD_DEFS_URL, { params })
			.then(({ data, headers }) => {
				if (generation !== generationRef.current) return;
				const list = Array.isArray(data) ? data : [];
				setCards(list);
				setTotalCount(readTotalCount(headers, list.length));
				setError(null);
			})
			.catch(err => {
				if (generation !== generationRef.current) return;
				if (query && err?.response?.status === 400 && onQueryError) {
					onQueryError(err.response?.data?.message);
					return;
				}
				setError(err);
				message.error('Failed to load cards');
			});
	}, [query, onQueryError, message]);

	// Any change in what the list means starts it over.
	useEffect(() => {
		const generation = ++generationRef.current;
		setLoading(true);
		fetchList(generation).finally(() => {
			if (generation === generationRef.current) setLoading(false);
		});
	}, [fetchList]);

	// A refetch in place (after a delete, say): no skeleton flash, the list
	// swaps when the response lands.
	const refresh = useCallback(() => {
		const generation = ++generationRef.current;
		return fetchList(generation);
	}, [fetchList]);

	return { cards, totalCount, loading, error, refresh };
}
